import { createMemo, createUniqueId, For, Show, type JSX } from "solid-js";

import clsx from "~/util/html/style";

import { useDeckWords } from "../deck_words";
import type { Arch, ArchAfter, ArchZoom, TeethDrawing } from "../types";
import styles from "./art.module.css";
import { useArtRef } from "./art_defs";
import {
  ARCH_HEIGHT,
  ARCH_MIDLINE,
  ARCH_SIDES,
  ARCH_TEETH,
  ARCH_WIDTH,
  GUM_BANDS,
  GUM_INNER_WIDTH,
  GUM_OUTER_WIDTH,
  formatUnits as f,
  type ArchTooth,
} from "./arch_geometry";

type ToothState = "lit" | "tinted" | "missing" | "restored";

const MAX_LABELS = 6;

// A callout is a pill of one or two lines of at most PILL_CHARS characters. Its width comes from an estimate of a
// character at the pill's 19px, since the text cannot be measured before it is drawn.
const PILL_CHARS = 16;
const CHAR_WIDTH = 10.4;
const PILL_PADDING = 17;
const PILL_HEIGHT = 38;
const LINE_HEIGHT = 22;
// Between a pill and the drawing's edge or the arch, and between two pills in a column.
const PILL_MARGIN = 6;
const PILL_GAP = 8;

// How far from a tooth's middle its leader starts, as a share of the tooth's half-width.
const LEAD_INSET = 0.7;
// One arch's crop runs this far past the midline, so the sides' names stay whole.
const JAW_MARGIN = 18;
// A close view of the planned teeth: this much around them, and never smaller than this, so one tooth still shows
// its neighbors.
const PLANNED_PADDING = 80;
// A close view's outer share on each side that fades out.
const FADE_EDGE = 0.12;
const PLANNED_MIN_WIDTH = 300;
const PLANNED_MIN_HEIGHT = 240;
// The smallest frame staff can drag (SlideEdits::MIN_FRAME), about two teeth across.
export const MIN_FRAME = 60;
// Callouts shrink with a close view so they read at about their whole-mouth size, but no smaller than this.
const MIN_PILL_SCALE = 0.55;

/** A view's size or place, to a tenth, written without a trailing ".0". */
const box = (value: number) => String(Math.round(value * 10) / 10);

/** The part of the drawing a view shows, in the arch's own units. */
export type ArchView = { x: number; y: number; width: number; height: number };

const WHOLE: ArchView = { x: 0, y: 0, width: ARCH_WIDTH, height: ARCH_HEIGHT };

/**
 * The teeth a drawing calls out, as many as it draws: one per tooth with that tooth's last text, in first-seen order,
 * at most six.
 */
export function archLabels(arch: Arch): Arch["labels"] {
  const byTooth = new Map(arch.labels.map(({ tooth, text }) => [tooth, text]));
  return [...byTooth]
    .filter(([tooth]) => ARCH_TEETH.some((t) => t.tooth === tooth))
    .slice(0, MAX_LABELS)
    .map(([tooth, text]) => ({ tooth, text }));
}

/** The callouts a drawing shows: those of teeth in its view. */
export function archLabelsInView(
  arch: Arch,
  zoom: ArchZoom = "both",
  drawing?: TeethDrawing,
): Arch["labels"] {
  const { view } = archFraming(arch, zoom, drawing);
  return focusedLabels(arch, drawing).filter(({ tooth }) => {
    const placed = ARCH_TEETH.find((t) => t.tooth === tooth);
    return placed !== undefined && inView(view, placed.x, placed.y);
  });
}

/** The callouts of the teeth that stand out, or every callout when none does. */
function focusedLabels(arch: Arch, drawing?: TeethDrawing): Arch["labels"] {
  const focus = drawing?.focus ?? [];
  const labels = archLabels(arch);
  return focus.length > 0 ? labels.filter(({ tooth }) => focus.includes(tooth)) : labels;
}

const inView = (view: ArchView, x: number, y: number) =>
  x >= view.x && x <= view.x + view.width && y >= view.y && y <= view.y + view.height;

/** What a drawing shows, and what kind of view that is: the whole mouth, one arch named whole, or a close view cut on
 *  every side, whose edges fade. */
export type ArchFraming = { view: ArchView; kind: "whole" | "upper" | "lower" | "close" };

/**
 * The part of the drawing staff or Ask Twelve framed: a frame staff dragged, else a close view of the teeth around,
 * else the zoom. A frame that fills the drawing is the whole mouth.
 */
export function archFraming(
  arch: Arch,
  zoom: ArchZoom = "both",
  drawing?: TeethDrawing,
): ArchFraming {
  if (drawing?.frame) {
    const view = clampFrame(drawing.frame);
    return { view, kind: isWhole(view) ? "whole" : "close" };
  }
  const around = drawing?.around ?? [];
  if (around.length > 0) {
    const view = viewAround(around);
    return { view, kind: isWhole(view) ? "whole" : "close" };
  }
  const view = archView(arch, zoom);
  if (zoom === "upper" || zoom === "lower") return { view, kind: zoom };
  return { view, kind: isWhole(view) ? "whole" : "close" };
}

const isWhole = (view: ArchView) => view.width >= ARCH_WIDTH && view.height >= ARCH_HEIGHT;

/** A frame kept inside the drawing and no smaller than MIN_FRAME each way. */
export function clampFrame(frame: ArchView): ArchView {
  const width = Math.min(ARCH_WIDTH, Math.max(MIN_FRAME, frame.width));
  const height = Math.min(ARCH_HEIGHT, Math.max(MIN_FRAME, frame.height));
  return {
    x: Math.min(ARCH_WIDTH - width, Math.max(0, frame.x)),
    y: Math.min(ARCH_HEIGHT - height, Math.max(0, frame.y)),
    width,
    height,
  };
}

/** A close view of some teeth with their neighbors; the whole mouth when none of them is drawn. */
export function viewAround(teeth: number[]): ArchView {
  const wanted = new Set(teeth);
  const placed = ARCH_TEETH.filter((t) => wanted.has(t.tooth));
  if (placed.length === 0) return WHOLE;
  const xs = placed.map((t) => t.x);
  const ys = placed.map((t) => t.y);
  const [x, width] = span(Math.min(...xs), Math.max(...xs), PLANNED_MIN_WIDTH, ARCH_WIDTH);
  const [y, height] = span(Math.min(...ys), Math.max(...ys), PLANNED_MIN_HEIGHT, ARCH_HEIGHT);
  return { x, y, width, height };
}

/**
 * The part of the drawing a zoom shows: the whole mouth, one arch, or the planned (lit or tinted) teeth with their
 * neighbors. A close view with no planned teeth shows the whole mouth.
 */
export function archView(arch: Arch, zoom: ArchZoom = "both"): ArchView {
  switch (zoom) {
    case "upper":
      return { x: 0, y: 0, width: ARCH_WIDTH, height: ARCH_MIDLINE + JAW_MARGIN };
    case "lower":
      return {
        x: 0,
        y: ARCH_MIDLINE - JAW_MARGIN,
        width: ARCH_WIDTH,
        height: ARCH_HEIGHT - ARCH_MIDLINE + JAW_MARGIN,
      };
    case "planned":
      return viewAround([...arch.lit, ...arch.tinted]);
    default:
      return WHOLE;
  }
}

/** One side of a close view: the teeth's extent padded, grown to the minimum about its middle, kept in the drawing.
 *  => [start, size] */
function span(low: number, high: number, min: number, full: number): [number, number] {
  const size = Math.min(full, Math.max(min, high - low + 2 * PLANNED_PADDING));
  return [Math.min(full - size, Math.max(0, (low + high) / 2 - size / 2)), size];
}

/**
 * Both arches from above, patient's right on the viewer's left. Planned teeth are lit, an area's teeth tinted and
 * missing teeth dashed; in "plain" mode the sides and jaws are named, and labels become callouts in side columns as
 * wide as the widest pill. A zoom or the drawing's frame crops it to one arch or a close view; there a side column
 * holds only the callouts on its side, and only the callouts of teeth in view show. The drawing can also fade every
 * tooth but its focus, hide the numbers, and show the mouth after treatment (`after`): planned teeth restored, the
 * teeth coming out gone. The words read their sizes from --arch-num, --arch-side and --arch-jaw where a layout sets
 * them.
 */
export function ArchArt(props: {
  arch: Arch;
  mode: "plain" | "dark" | "mini";
  numbers?: boolean;
  title: string;
  zoom?: ArchZoom;
  drawing?: TeethDrawing;
  after?: ArchAfter;
}): JSX.Element {
  const words = useDeckWords();
  const afterTreatment = () => (props.drawing?.moment === "after" ? props.after : undefined);
  const lit = createMemo(() => new Set(afterTreatment() ? [] : props.arch.lit));
  const tinted = createMemo(() => new Set(afterTreatment() ? [] : props.arch.tinted));
  const restored = createMemo(() => new Set(afterTreatment()?.restored ?? []));
  const missing = createMemo(() => new Set(afterTreatment()?.missing ?? props.arch.missing));
  const focus = createMemo(() => new Set(props.drawing?.focus ?? []));
  const state = (tooth: number): ToothState | undefined => {
    if (missing().has(tooth)) return "missing";
    if (lit().has(tooth)) return "lit";
    if (restored().has(tooth)) return "restored";
    if (tinted().has(tooth)) return "tinted";
    return undefined;
  };
  const faded = (tooth: number) => focus().size > 0 && !focus().has(tooth);
  // A tinted tooth keeps the plain number color.
  const numberClass = (tooth: number) => {
    const toothState = state(tooth);
    return clsx(
      toothState !== "tinted" && toothState !== undefined && styles[toothState],
      faded(tooth) && styles["faded"],
    );
  };
  const zoom = () => props.zoom ?? "both";
  // The view's clip, and the fade a close view gets, since it is cut on every side.
  const clip = `arch-clip-${createUniqueId()}`;
  const fade = `${clip}-fade`;
  const framing = createMemo(() => archFraming(props.arch, zoom(), props.drawing));
  const view = () => framing().view;
  const close = () => framing().kind === "close";
  const layout = createMemo(() =>
    layOutCallouts(focusedLabels(props.arch, props.drawing), view(), framing().kind !== "whole"),
  );
  const width = () => view().width + layout().left + layout().right;
  // The jaws' and sides' names, in a view they fit whole.
  const named = (jaw: "upper" | "lower") => framing().kind === "whole" || framing().kind === jaw;
  return (
    <svg
      viewBox={`0 0 ${box(width())} ${box(view().height)}`}
      class={clsx(styles["arch"], styles[`arch-${props.mode}`])}
      role="img"
      aria-label={props.title}
      data-zoom={zoom()}
      data-framing={framing().kind}
    >
      <title>{props.title}</title>
      {/* Clipped to the view, so a cropped arch never runs into the callouts. A group rather than a nested svg, which
          a layout's own svg sizing rules would resize. */}
      <g transform={`translate(${box(layout().left - view().x)} ${box(-view().y)})`}>
        <defs>
          <clipPath id={clip}>
            <rect
              x={box(view().x)}
              y={box(view().y)}
              width={box(view().width)}
              height={box(view().height)}
            />
          </clipPath>
        </defs>
        <Show when={close()}>
          <defs>
            <For each={["x", "y"] as const}>
              {(axis) => (
                <>
                  <linearGradient
                    id={`${fade}-${axis}-g`}
                    x2={axis === "x" ? "1" : "0"}
                    y2={axis === "y" ? "1" : "0"}
                  >
                    <stop offset="0" stop-color="#000000" />
                    <stop offset={FADE_EDGE} stop-color="#FFFFFF" />
                    <stop offset={1 - FADE_EDGE} stop-color="#FFFFFF" />
                    <stop offset="1" stop-color="#000000" />
                  </linearGradient>
                  <mask
                    id={`${fade}-${axis}`}
                    maskUnits="userSpaceOnUse"
                    x={box(view().x)}
                    y={box(view().y)}
                    width={box(view().width)}
                    height={box(view().height)}
                  >
                    <rect
                      x={box(view().x)}
                      y={box(view().y)}
                      width={box(view().width)}
                      height={box(view().height)}
                      fill={`url(#${fade}-${axis}-g)`}
                    />
                  </mask>
                </>
              )}
            </For>
          </defs>
        </Show>
        {/* One mask per side pair, nested, so only the outer edges fade and the teeth in the middle stay whole. */}
        <g clip-path={`url(#${clip})`} mask={close() ? `url(#${fade}-x)` : undefined}>
          <g mask={close() ? `url(#${fade}-y)` : undefined}>
            <For each={GUM_BANDS}>
              {(band) => (
                <>
                  <path
                    d={band}
                    class={styles["gum-o"]}
                    fill="none"
                    stroke-width={GUM_OUTER_WIDTH}
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d={band}
                    class={styles["gum-i"]}
                    fill="none"
                    stroke-width={GUM_INNER_WIDTH}
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </>
              )}
            </For>
            <For each={ARCH_TEETH.filter((t) => lit().has(t.tooth) && !faded(t.tooth))}>
              {(t) => (
                <circle
                  cx={f(t.x)}
                  cy={f(t.y)}
                  r={f(t.width * 0.95)}
                  class={styles["t-halo"]}
                  filter={useArtRef("f-blur6")}
                />
              )}
            </For>
            <For each={ARCH_TEETH}>
              {(t) => <ArchToothShape tooth={t} state={state(t.tooth)} faded={faded(t.tooth)} />}
            </For>
            {/* An offset in em keeps a number or a side's name centered at whatever size the layout sets. */}
            <Show when={(props.numbers ?? true) && props.drawing?.numbers !== false}>
              <For each={ARCH_TEETH}>
                {(t) => (
                  <text
                    x={f(t.x)}
                    y={f(t.y)}
                    dy="0.32em"
                    class={clsx(styles["t-num"], numberClass(t.tooth))}
                    text-anchor="middle"
                  >
                    {t.tooth}
                  </text>
                )}
              </For>
            </Show>
            <Show when={props.mode === "plain" && !close()}>
              <text
                x={ARCH_SIDES[0]}
                y={ARCH_MIDLINE}
                dy="0.36em"
                class={styles["a-side"]}
                text-anchor="middle"
              >
                {words().t("yourRight")}
              </text>
              <text
                x={ARCH_SIDES[1]}
                y={ARCH_MIDLINE}
                dy="0.36em"
                class={styles["a-side"]}
                text-anchor="middle"
              >
                {words().t("yourLeft")}
              </text>
              <Show when={named("upper")}>
                <text
                  x={ARCH_WIDTH / 2}
                  y={ARCH_MIDLINE - 7}
                  class={styles["a-jaw"]}
                  text-anchor="middle"
                >
                  {words().t("upper")}
                </text>
              </Show>
              <Show when={named("lower")}>
                <text
                  x={ARCH_WIDTH / 2}
                  y={ARCH_MIDLINE + 21}
                  class={styles["a-jaw"]}
                  text-anchor="middle"
                >
                  {words().t("lower")}
                </text>
              </Show>
            </Show>
          </g>
        </g>
      </g>
      <For each={layout().callouts}>
        {(c) => (
          <g>
            <Show when={c.cut}>
              <title>{c.text}</title>
            </Show>
            <path
              d={`M${f(c.x)},${f(c.y)} C${f((c.x + c.edge) / 2)},${f(c.y)} ${f((c.x + c.edge) / 2)},${f(c.labelY)} ${f(c.edge)},${f(c.labelY)}`}
              class={styles["lead"]}
              fill="none"
            />
            <circle
              cx={f(c.x)}
              cy={f(c.y)}
              r={f(4.2 * layout().scale)}
              class={styles["lead-dot"]}
            />
            <rect
              x={f(c.boxX)}
              y={f(c.labelY - c.height / 2)}
              width={f(c.width)}
              height={f(c.height)}
              rx={f(19 * layout().scale)}
              class={styles["pill"]}
            />
            {/* Scaled about the pill's middle, so its words shrink with it. */}
            <text
              class={styles["pill-t"]}
              text-anchor="middle"
              transform={
                layout().scale === 1
                  ? undefined
                  : `translate(${f(c.boxX + c.width / 2)} ${f(c.labelY)}) scale(${f(layout().scale, 3)})`
              }
            >
              <For each={c.lines}>
                {(line, i) => (
                  <tspan
                    x={f(layout().scale === 1 ? c.boxX + c.width / 2 : 0)}
                    y={f(
                      (layout().scale === 1 ? c.labelY : 0) +
                        6.6 +
                        LINE_HEIGHT * (i() - (c.lines.length - 1) / 2),
                    )}
                  >
                    {line}
                  </tspan>
                )}
              </For>
            </text>
          </g>
        )}
      </For>
    </svg>
  );
}

/** One tooth seen from above, placed and turned along the arch. */
export function ArchToothShape(props: {
  tooth: ArchTooth;
  state?: ToothState;
  faded?: boolean;
}): JSX.Element {
  return (
    <g
      class={clsx(styles["t"], props.state && styles[props.state], props.faded && styles["faded"])}
      transform={`translate(${f(props.tooth.x)} ${f(props.tooth.y)}) rotate(${f(props.tooth.angle)})`}
    >
      <path d={props.tooth.outline} class={styles["t-shadow"]} transform="translate(0 1.6)" />
      <path d={props.tooth.outline} class={styles["t-body"]} fill={useArtRef("g-occ")} />
      <path d={props.tooth.grooves} class={styles["t-groove"]} />
    </g>
  );
}

type Pill = {
  tooth: number;
  text: string;
  lines: string[];
  /** The lines leave part of the text out. */
  cut: boolean;
  width: number;
  height: number;
};

type Callout = Pill & {
  /** Where the leader leaves the tooth, at its edge toward the pill, in the full drawing (label columns included). */
  x: number;
  y: number;
  labelY: number;
  boxX: number;
  /** Where the leader line meets the pill. */
  edge: number;
};

/**
 * Pills in side columns as wide as the widest, each on its tooth's side of the view's middle, spaced apart and kept
 * inside the drawing, with a leader line to the tooth. The whole mouth keeps both columns so the arch stays centered;
 * a zoomed view (`own`) keeps only the columns it uses, shows only the teeth in view, and shrinks the pills with the
 * view. No labels, no columns.
 */
function layOutCallouts(
  labels: Arch["labels"],
  view: ArchView,
  own: boolean,
): { left: number; right: number; scale: number; callouts: Callout[] } {
  const scale = own ? Math.min(1, Math.max(MIN_PILL_SCALE, view.height / ARCH_HEIGHT)) : 1;
  const margin = PILL_MARGIN * scale;
  const pills = labels
    .filter(({ tooth }) => {
      const placed = ARCH_TEETH.find((t) => t.tooth === tooth);
      return placed !== undefined && inView(view, placed.x, placed.y);
    })
    .map(({ tooth, text }) => pill(tooth, text, scale));
  if (pills.length === 0) return { left: 0, right: 0, scale, callouts: [] };
  const columnWidth = (items: Pill[]) =>
    items.length ? Math.ceil(Math.max(...items.map((p) => p.width)) + 2 * margin) : 0;
  const middle = view.x + view.width / 2;
  const sideOf = (p: Pill) =>
    (ARCH_TEETH.find((t) => t.tooth === p.tooth)?.x ?? 0) < middle ? "L" : "R";
  const both = columnWidth(pills);
  const left = own ? columnWidth(pills.filter((p) => sideOf(p) === "L")) : both;
  const right = own ? columnWidth(pills.filter((p) => sideOf(p) === "R")) : both;
  const width = view.width + left + right;
  const columns: Record<"L" | "R", (Pill & { x: number; y: number })[]> = { L: [], R: [] };
  for (const p of pills) {
    const placed = ARCH_TEETH.find((t) => t.tooth === p.tooth);
    if (!placed) continue;
    // The leader starts at the tooth's edge toward its pill, so its dot never covers the tooth's number.
    const toward = sideOf(p) === "L" ? -1 : 1;
    columns[sideOf(p)].push({
      ...p,
      x: placed.x + toward * placed.width * LEAD_INSET - view.x + left,
      y: placed.y - view.y,
    });
  }
  const gap = PILL_GAP * scale;
  const callouts = (["L", "R"] as const).flatMap((column) => {
    const items = columns[column].sort((a, b) => a.y - b.y || a.x - b.x || a.tooth - b.tooth);
    const apart = (k: number) => ((items[k - 1]?.height ?? 0) + (items[k]?.height ?? 0)) / 2 + gap;
    // Down from the top past each neighbor, then back up from the bottom, so every pill stays in the drawing.
    const ys: number[] = [];
    items.forEach((item, k) => {
      const floor = k === 0 ? item.height / 2 + margin : (ys[k - 1] ?? 0) + apart(k);
      ys[k] = Math.max(item.y, floor);
    });
    for (let k = items.length - 1; k >= 0; k--) {
      const item = items[k];
      if (!item) continue;
      const ceiling =
        k === items.length - 1
          ? view.height - item.height / 2 - margin
          : (ys[k + 1] ?? 0) - apart(k + 1);
      ys[k] = Math.min(ys[k] ?? item.y, ceiling);
    }
    return items.map((item, k) => {
      const boxX = column === "L" ? margin : width - margin - item.width;
      return {
        ...item,
        labelY: ys[k] ?? item.y,
        boxX,
        edge: column === "L" ? boxX + item.width : boxX,
      };
    });
  });
  return { left, right, scale, callouts };
}

function pill(tooth: number, text: string, scale: number): Pill {
  const { lines, cut } = pillLines(text);
  const longest = Math.max(...lines.map((line) => line.length));
  return {
    tooth,
    text,
    lines,
    cut,
    width: (2 * PILL_PADDING + CHAR_WIDTH * longest) * scale,
    height: (PILL_HEIGHT + LINE_HEIGHT * (lines.length - 1)) * scale,
  };
}

/**
 * A label on one line when it fits; else two, broken before its " + " when both titles fit, or after the last word
 * that does; past two lines the second ends in an ellipsis.
 */
function pillLines(text: string): { lines: string[]; cut: boolean } {
  if (text.length <= PILL_CHARS) return { lines: [text], cut: false };
  const plus = text.indexOf(" + ");
  if (plus > 0 && plus <= PILL_CHARS && text.length - plus - 1 <= PILL_CHARS) {
    return { lines: [text.slice(0, plus), text.slice(plus + 1)], cut: false };
  }
  // A line that would end on the "+" leaves it to start the next.
  const first = wordsThatFit(text, PILL_CHARS).replace(/ \+$/u, "");
  const rest = text.slice(first.length).trimStart();
  if (rest.length <= PILL_CHARS) return { lines: [first, rest], cut: false };
  const second = wordsThatFit(rest, PILL_CHARS - 1).replace(/[\s,+]+$/u, "");
  return { lines: [first, `${second}…`], cut: true };
}

/** The words of `text` that fit in `max` characters, or its first `max` characters when the first word does not. */
function wordsThatFit(text: string, max: number): string {
  const space = text.lastIndexOf(" ", max);
  return space > 0 ? text.slice(0, space) : text.slice(0, max);
}
